<template>
  <div>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-1">
          Preferences
        </h3>
        <p class="text--secondary mb-0">
          Customize your application experience and display preferences.
        </p>
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-form @submit.prevent="handleSubmit">
      <!-- Appearance Section -->
      <v-row
        class="mb-2"
        align="center"
      >
        <v-col class="col-auto pr-2">
          <font-awesome-icon
            :icon="['fas', 'circle-half-stroke']"
            class="text--secondary"
          />
        </v-col>
        <v-col class="pa-0">
          <span class="subtitle-1 font-weight-medium">Appearance</span>
        </v-col>
      </v-row>

      <v-row class="mb-2">
        <v-col class="py-0">
          <p class="caption text--secondary mb-0">
            Control how the application looks for you.
            <span v-if="localPreferences.theme === 'system'">
              Currently following your device's setting.
            </span>
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="localPreferences.theme"
            :items="themeOptions"
            label="Theme"
            outlined
            dense
            @change="handleThemeChange"
          >
            <template v-slot:item="{ item }">
              <font-awesome-icon
                :icon="themeIcon(item.value)"
                class="mr-3 text--secondary"
                size="sm"
              />
              {{ item.text }}
            </template>
            <template v-slot:selection="{ item }">
              <font-awesome-icon
                :icon="themeIcon(item.value)"
                class="mr-2 text--secondary"
                size="sm"
              />
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-divider class="my-6" />

      <!-- Language & Region Section -->
      <v-row
        class="mb-2"
        align="center"
      >
        <v-col class="col-auto pr-2">
          <font-awesome-icon
            :icon="['fas', 'earth-americas']"
            class="text--secondary"
          />
        </v-col>
        <v-col class="pa-0">
          <span class="subtitle-1 font-weight-medium">Language &amp; Region</span>
        </v-col>
      </v-row>

      <v-row class="mb-2">
        <v-col class="py-0">
          <p class="caption text--secondary mb-0">
            Set your preferred language, timezone, and date formats.
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="localPreferences.language"
            :items="languageOptions"
            label="Language"
            outlined
            dense
            hint="Default: English"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="localPreferences.timezone"
            :items="timezoneOptions"
            label="Timezone"
            outlined
            dense
            hint="Default: UTC"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="localPreferences.dateFormat"
            :items="dateFormatOptions"
            label="Date Format"
            outlined
            dense
            hint="Default: MM/DD/YYYY"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="localPreferences.timeFormat"
            :items="timeFormatOptions"
            label="Time Format"
            outlined
            dense
            hint="Default: 12-hour (AM/PM)"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-divider class="mt-4 mb-6" />

      <v-row>
        <v-col class="d-flex justify-end">
          <v-btn
            color="primary"
            outlined
            class="text-none"
            :disabled="!hasChanges"
            :loading="loading"
            @click="handleSubmit"
          >
            Update Preferences
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
export default {
  name: 'SettingsPreferences',
  props: {
    preferences: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localPreferences: {},
      themeOptions: [
        { text: 'Light', value: 'light' },
        { text: 'Dark', value: 'dark' },
        { text: 'System (follow device)', value: 'system' },
      ],
      languageOptions: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' },
        { text: 'French', value: 'fr' },
      ],
      timezoneOptions: [
        { text: 'UTC', value: 'UTC' },
        { text: 'Eastern Time', value: 'America/New_York' },
        { text: 'Central Time', value: 'America/Chicago' },
        { text: 'Mountain Time', value: 'America/Denver' },
        { text: 'Pacific Time', value: 'America/Los_Angeles' },
      ],
      dateFormatOptions: [
        { text: 'MM/DD/YYYY', value: 'MM/dd/yyyy' },
        { text: 'DD/MM/YYYY', value: 'dd/MM/yyyy' },
        { text: 'YYYY-MM-DD', value: 'yyyy-MM-dd' },
      ],
      timeFormatOptions: [
        { text: '12-hour (AM/PM)', value: '12h' },
        { text: '24-hour', value: '24h' },
      ],
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.localPreferences) !== JSON.stringify(this.preferences)
    },
    layoutDarkMode() {
      return this.$store.state.layout?.darkMode
    },
  },
  watch: {
    preferences: {
      handler(newPreferences) {
        this.localPreferences = { ...newPreferences }
      },
      immediate: true,
      deep: true,
    },
    // Keep dropdown in sync when dark mode is toggled externally (e.g., from SettingsModal).
    // Skip sync when user has chosen 'system' — that's intentionally OS-driven.
    layoutDarkMode(isDark) {
      const current = this.localPreferences.theme
      if (current === 'system') return
      const synced = isDark ? 'dark' : 'light'
      if (current !== synced) {
        this.localPreferences = { ...this.localPreferences, theme: synced }
      }
    },
  },
  methods: {
    handleSubmit() {
      if (this.hasChanges) {
        this.$emit('update', this.localPreferences)
      }
    },
    handleThemeChange(theme) {
      this.$emit('theme-change', theme)
    },
    themeIcon(value) {
      const icons = {
        light: ['fas', 'sun'],
        dark: ['fas', 'moon'],
        system: ['fas', 'display'],
      }
      return icons[value] || ['fas', 'circle-half-stroke']
    },
  },
}
</script>
